import { useState } from 'react';
import { Trash2, Link2, Palette } from 'lucide-react';
import { useStore } from '../store/useStore.js';
import { NEON_PALETTE } from '../utils/constants.js';

export function MultiSelectBar() {
  const multiSelected = useStore((s) => s.multiSelected);
  const deleteNode = useStore((s) => s.deleteNode);
  const updateNode = useStore((s) => s.updateNode);
  const createLink = useStore((s) => s.createLink);
  const setMultiSelected = useStore((s) => s.setMultiSelected);
  const [colorOpen, setColorOpen] = useState(false);

  const count = multiSelected.size;
  if (count < 2) return null;

  function handleDelete() {
    [...multiSelected].forEach((id) => deleteNode(id));
    setMultiSelected(new Set());
  }

  function handleColor(color: string) {
    [...multiSelected].forEach((id) => updateNode(id, { color }));
    setColorOpen(false);
  }

  function handleConnectAll() {
    const ids = [...multiSelected];
    for (let i = 0; i < ids.length; i++) {
      for (let j = i + 1; j < ids.length; j++) {
        createLink(ids[i]!, ids[j]!, 'synapse');
      }
    }
    setMultiSelected(new Set());
  }

  return (
    <div
      style={{
        position: 'fixed',
        top: 'calc(var(--topbar-h) + 10px)',
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        background: 'rgba(2, 6, 20, 0.88)',
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(0,245,255,0.25)',
        borderRadius: 100,
        padding: '6px 12px 6px 16px',
        zIndex: 85,
        boxShadow: '0 4px 24px rgba(0,0,0,0.5)',
      }}
    >
      <span style={{
        fontSize: 12, fontWeight: 700,
        color: 'var(--neon)',
        paddingRight: 8,
        borderRight: '1px solid var(--border)',
        marginRight: 2,
        whiteSpace: 'nowrap',
      }}>
        {count} nós selecionados
      </span>

      {/* Connect all */}
      <BarBtn label="Conectar todos" onClick={handleConnectAll}>
        <Link2 size={13} />
        <span style={{ fontSize: 11, marginLeft: 4 }}>Conectar</span>
      </BarBtn>

      {/* Color picker */}
      <div style={{ position: 'relative' }}>
        <BarBtn label="Mudar cor" onClick={() => setColorOpen((v) => !v)}>
          <Palette size={13} />
          <span style={{ fontSize: 11, marginLeft: 4 }}>Cor</span>
        </BarBtn>
        {colorOpen && (
          <div
            className="glass-panel"
            style={{
              position: 'absolute',
              top: '100%',
              left: '50%',
              transform: 'translateX(-50%)',
              marginTop: 8,
              padding: '8px 10px',
              borderRadius: 10,
              display: 'flex',
              flexWrap: 'wrap',
              gap: 6,
              width: 140,
              zIndex: 200,
            }}
          >
            {NEON_PALETTE.map((c) => (
              <button
                key={c}
                onClick={() => handleColor(c)}
                style={{
                  width: 18, height: 18, borderRadius: '50%',
                  background: c, border: '2px solid transparent',
                  cursor: 'pointer', padding: 0,
                  transition: 'transform 0.1s',
                }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1.3)'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1)'; }}
              />
            ))}
          </div>
        )}
      </div>

      <div style={{ width: 1, height: 18, background: 'var(--border)', margin: '0 2px' }} />

      {/* Delete */}
      <BarBtn label="Apagar selecionados" onClick={handleDelete} danger>
        <Trash2 size={13} />
        <span style={{ fontSize: 11, marginLeft: 4 }}>Apagar</span>
      </BarBtn>

      {/* Dismiss */}
      <button
        onClick={() => setMultiSelected(new Set())}
        title="Limpar seleção (Esc)"
        style={{
          marginLeft: 4,
          background: 'none', border: 'none',
          color: 'var(--muted)', cursor: 'pointer',
          fontSize: 16, lineHeight: 1, padding: '0 2px',
        }}
      >
        ×
      </button>
    </div>
  );
}

function BarBtn({
  children, label, onClick, danger,
}: { children: React.ReactNode; label: string; onClick: () => void; danger?: boolean }) {
  return (
    <button
      title={label}
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center',
        padding: '5px 10px', border: '1px solid var(--border)',
        borderRadius: 20, background: 'transparent',
        color: danger ? '#f87171' : 'var(--text)',
        cursor: 'pointer', transition: 'all 0.15s',
        fontSize: 12,
      }}
      onMouseEnter={(e) => {
        const el = e.currentTarget as HTMLButtonElement;
        el.style.background = danger ? 'rgba(248,113,113,0.12)' : 'rgba(255,255,255,0.07)';
        el.style.borderColor = danger ? 'rgba(248,113,113,0.4)' : 'rgba(0,245,255,0.3)';
      }}
      onMouseLeave={(e) => {
        const el = e.currentTarget as HTMLButtonElement;
        el.style.background = 'transparent';
        el.style.borderColor = 'var(--border)';
      }}
    >
      {children}
    </button>
  );
}
